import { useMemo } from "react";
import { useAllWfh, useMyWfh, useTeamWfh } from "@/api/queries";
import { useSession } from "@/api/session";
import { Avatar } from "@/components/ui/avatar";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import { formatDate } from "@/lib/format";
import { todayStr } from "@/lib/calendar";

const LIMIT = 8;

/**
 * Who is working from home, soonest first — on the org calendar, next to the
 * holidays and birthdays, because "who will be in the office on Thursday" is a
 * calendar question and this is the calendar.
 *
 * Scoped by role, and the scope is the point:
 *
 *   employee  their own approved days only. The org calendar is visible to
 *             everybody, and turning it into a list of where every colleague
 *             will be sitting is a different product decision from the one that
 *             was asked for.
 *   manager   their own team.
 *   admin     everyone.
 *
 * Each role reads the endpoint it is already entitled to; the backend enforces
 * that, so nothing here can widen it.
 */
export function UpcomingWfhPanel() {
  const role = useSession((state) => state.user?.role);
  const isManager = role === "manager";
  const isAdmin = role === "admin";

  const mine = useMyWfh();
  const team = useTeamWfh(isManager);
  const org = useAllWfh(isAdmin);

  const source = isAdmin ? org : isManager ? team : mine;
  const showPerson = isAdmin || isManager;

  const upcoming = useMemo(() => {
    const today = todayStr();
    return (source.data ?? [])
      .filter((request) => request.status === "approved" && request.end_date >= today)
      .sort((a, b) => a.start_date.localeCompare(b.start_date))
      .slice(0, LIMIT);
  }, [source.data]);

  const scopeNote = isAdmin
    ? "Approved, org-wide"
    : isManager
      ? "Approved, your team"
      : "Approved, your own days";

  return (
    <Panel className="min-w-0">
      <PanelHeader title="Working from home next" note={scopeNote} />
      {source.isLoading ? (
        <SkeletonTable rows={4} cols={2} />
      ) : upcoming.length === 0 ? (
        <EmptyState
          icon="wfh"
          title="Nothing coming up"
          body={
            showPerson
              ? "No approved work-from-home days are still ahead."
              : "You have no approved work-from-home days still ahead."
          }
        />
      ) : (
        <ul className="divide-y divide-line">
          {upcoming.map((request) => (
            <li key={request.id} className="flex items-center gap-3 px-4 py-3">
              {showPerson ? (
                <Avatar name={request.employee_name} size="sm" />
              ) : (
                <Icon name="wfh" size={20} className="shrink-0 text-faint" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">
                  {showPerson ? (request.employee_name ?? "Unnamed") : request.reason}
                </p>
                <p className="font-mono text-xs text-muted">
                  {formatDate(request.start_date, "d MMM")}
                  {request.start_date !== request.end_date &&
                    ` to ${formatDate(request.end_date, "d MMM")}`}
                  {` · ${request.days} ${request.days === 1 ? "day" : "days"}`}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
